'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type Lang = 'en' | 'ar' | 'fr';

const translations = {
  en: {
    dir: 'ltr' as const,
    back: 'Back to Home',
    badge: 'Refund & Cancellation Policy',
    title: 'Refund & Cancellation Policy',
    updated: 'Last updated: August 2026',
    sections: [
      {
        heading: '1. Free Trial',
        body: 'WealthOS offers a 7-day free trial for new users on paid plans. No credit card is required to start the trial. You will not be charged during the trial period. If you do not cancel before the trial ends, your selected plan will be activated and payment will be processed.',
      },
      {
        heading: '2. Cancellation Policy',
        body: 'You may cancel your WealthOS subscription at any time from Settings → Subscription → Manage Plan. Cancellation takes effect at the end of your current billing period. You will continue to have access to your paid plan features until the end of the billing period. No partial refunds are issued for unused time within a billing period.',
      },
      {
        heading: '3. Refund Policy',
        body: 'We offer a full refund within 7 days of your initial subscription payment if you are not satisfied with WealthOS. To request a refund, contact our support team at support@wealthos.app within 7 days of your first payment. Refund requests after 7 days are evaluated on a case-by-case basis at our discretion.',
      },
      {
        heading: '4. Non-Refundable Items',
        body: 'Lifetime plan purchases are non-refundable after 7 days. Subscription renewals are non-refundable once processed. Promotional or discounted purchases may have different refund terms as specified at the time of purchase.',
      },
      {
        heading: '5. Billing Disputes',
        body: 'If you believe you have been charged incorrectly, please contact us at support@wealthos.app within 30 days of the charge. We will investigate and resolve billing disputes promptly.',
      },
      {
        heading: '6. Plan Downgrades',
        body: 'You may downgrade your plan at any time. Downgrades take effect at the start of the next billing period. No refund is issued for the difference in plan cost during the current billing period.',
      },
      {
        heading: '7. Account Deletion',
        body: 'If you delete your account, your subscription will be cancelled immediately. No refund will be issued for the remaining period unless you are within the 7-day refund window.',
      },
      {
        heading: '8. Contact',
        body: 'For refund requests or billing questions, please contact us at support@wealthos.app or through the Support page. We aim to respond within 24 hours on business days.',
      },
    ],
  },
  ar: {
    dir: 'rtl' as const,
    back: 'العودة للرئيسية',
    badge: 'سياسة الاسترداد والإلغاء',
    title: 'سياسة الاسترداد والإلغاء',
    updated: 'آخر تحديث: أغسطس 2026',
    sections: [
      {
        heading: '١. الفترة التجريبية المجانية',
        body: 'يقدم WealthOS تجربة مجانية لمدة 7 أيام للمستخدمين الجدد على الخطط المدفوعة. لا تحتاج إلى بطاقة ائتمانية لبدء التجربة. لن يتم تحصيل أي رسوم خلال فترة التجربة. إذا لم تلغِ قبل انتهاء التجربة، سيتم تفعيل خطتك المختارة ومعالجة الدفع.',
      },
      {
        heading: '٢. سياسة الإلغاء',
        body: 'يمكنك إلغاء اشتراكك في WealthOS في أي وقت من الإعدادات ← الاشتراك ← إدارة الخطة. يسري الإلغاء في نهاية فترة الفوترة الحالية. ستحتفظ بالوصول إلى مميزات خطتك المدفوعة حتى نهاية فترة الفوترة. لا تُصدر استردادات جزئية للوقت غير المستخدم خلال فترة الفوترة.',
      },
      {
        heading: '٣. سياسة الاسترداد',
        body: 'نقدم استرداداً كاملاً خلال 7 أيام من دفعة اشتراكك الأولى إذا لم تكن راضياً عن WealthOS. لطلب الاسترداد، تواصل مع فريق الدعم على support@wealthos.app خلال 7 أيام من دفعتك الأولى. تُقيَّم طلبات الاسترداد بعد 7 أيام على أساس كل حالة على حدة وفق تقديرنا.',
      },
      {
        heading: '٤. العناصر غير القابلة للاسترداد',
        body: 'مشتريات الخطة مدى الحياة غير قابلة للاسترداد بعد 7 أيام. تجديدات الاشتراك غير قابلة للاسترداد بعد معالجتها. قد تخضع المشتريات الترويجية أو المخفضة لشروط استرداد مختلفة كما هو محدد وقت الشراء.',
      },
      {
        heading: '٥. نزاعات الفوترة',
        body: 'إذا كنت تعتقد أنه تم تحصيل رسوم منك بشكل غير صحيح، يرجى التواصل معنا على support@wealthos.app خلال 30 يوماً من الرسوم. سنحقق في نزاعات الفوترة ونحلها على الفور.',
      },
      {
        heading: '٦. تخفيض الخطة',
        body: 'يمكنك تخفيض خطتك في أي وقت. يسري التخفيض في بداية فترة الفوترة التالية. لا يُصدر أي استرداد للفرق في تكلفة الخطة خلال فترة الفوترة الحالية.',
      },
      {
        heading: '٧. حذف الحساب',
        body: 'إذا حذفت حسابك، سيتم إلغاء اشتراكك فوراً. لن يُصدر أي استرداد للفترة المتبقية ما لم تكن ضمن نافذة الاسترداد لمدة 7 أيام.',
      },
      {
        heading: '٨. التواصل',
        body: 'لطلبات الاسترداد أو أسئلة الفوترة، يرجى التواصل معنا على support@wealthos.app أو عبر صفحة الدعم. نهدف إلى الرد خلال 24 ساعة في أيام العمل.',
      },
    ],
  },
  fr: {
    dir: 'ltr' as const,
    back: "Retour à l'accueil",
    badge: 'Politique de remboursement et d\'annulation',
    title: 'Politique de remboursement et d\'annulation',
    updated: 'Dernière mise à jour : Août 2026',
    sections: [
      {
        heading: '1. Essai gratuit',
        body: "WealthOS offre un essai gratuit de 7 jours pour les nouveaux utilisateurs sur les plans payants. Aucune carte bancaire n'est requise pour commencer l'essai. Vous ne serez pas facturé pendant la période d'essai. Si vous n'annulez pas avant la fin de l'essai, votre plan sélectionné sera activé et le paiement sera traité.",
      },
      {
        heading: '2. Politique d\'annulation',
        body: "Vous pouvez annuler votre abonnement WealthOS à tout moment depuis Paramètres → Abonnement → Gérer le plan. L\'annulation prend effet à la fin de votre période de facturation actuelle. Vous continuerez à avoir accès aux fonctionnalités de votre plan payant jusqu\'à la fin de la période de facturation. Aucun remboursement partiel n\'est émis pour le temps non utilisé dans une période de facturation.",
      },
      {
        heading: '3. Politique de remboursement',
        body: "Nous offrons un remboursement complet dans les 7 jours suivant votre premier paiement d'abonnement si vous n'êtes pas satisfait de WealthOS. Pour demander un remboursement, contactez notre équipe de support à support@wealthos.app dans les 7 jours suivant votre premier paiement. Les demandes de remboursement après 7 jours sont évaluées au cas par cas à notre discrétion.",
      },
      {
        heading: '4. Articles non remboursables',
        body: "Les achats de plan à vie ne sont pas remboursables après 7 jours. Les renouvellements d'abonnement ne sont pas remboursables une fois traités. Les achats promotionnels ou à prix réduit peuvent avoir des conditions de remboursement différentes telles que spécifiées au moment de l'achat.",
      },
      {
        heading: '5. Litiges de facturation',
        body: "Si vous pensez avoir été facturé incorrectement, veuillez nous contacter à support@wealthos.app dans les 30 jours suivant la facturation. Nous enquêterons et résoudrons rapidement les litiges de facturation.",
      },
      {
        heading: '6. Déclassement de plan',
        body: "Vous pouvez déclasser votre plan à tout moment. Les déclassements prennent effet au début de la prochaine période de facturation. Aucun remboursement n'est émis pour la différence de coût du plan pendant la période de facturation actuelle.",
      },
      {
        heading: '7. Suppression de compte',
        body: "Si vous supprimez votre compte, votre abonnement sera annulé immédiatement. Aucun remboursement ne sera émis pour la période restante, sauf si vous êtes dans la fenêtre de remboursement de 7 jours.",
      },
      {
        heading: '8. Contact',
        body: "Pour les demandes de remboursement ou les questions de facturation, veuillez nous contacter à support@wealthos.app ou via la page Support. Nous visons à répondre dans les 24 heures les jours ouvrables.",
      },
    ],
  },
};

export default function RefundPolicyPage() {
  const [lang, setLang] = useState<Lang>('en');
  const t = translations[lang];
  const isRtl = t.dir === 'rtl';

  return (
    <div className="min-h-screen bg-background" dir={t.dir}>
      <nav className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/welcome" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} className={isRtl ? 'rotate-180' : ''} />
            {t.back}
          </Link>
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-semibold text-foreground">Wealth<span className="text-primary">OS</span></span>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['en', 'ar', 'fr'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  lang === l ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5">
            <RotateCcw size={13} className="text-primary" />
            <span className="text-xs font-semibold text-primary">{t.badge}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.updated}</p>
        </div>

        <div className="space-y-6">
          {t.sections.map((section) => (
            <div key={section.heading} className="glass-card rounded-2xl p-6 border border-border">
              <h2 className="text-base font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
          <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <Link href="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link>
          <Link href="/refund-policy" className="text-primary font-semibold">Refund Policy</Link>
          <Link href="/support" className="hover:text-foreground transition-colors">Support</Link>
        </div>
      </main>
    </div>
  );
}
